<template>
  <el-drawer
    v-model="visible"
    :close-on-click-modal="false"
    :modal="false"
    :append-to-body="false"
    :fullscreen="false"
    :size="drawerWidthPct"
    class="create-workflow-drawer"
    modal-class="create-workflow-drawer"
  >
    <template #header>
      <div class="create-workflow-drawer__header">
        <h4 class="scripty">Create new workflow<template v-if="!makeEntityAgnostic"> for this {{ TextAssistant.entitySingularName(entityType).toLowerCase() }}</template>&hellip;</h4>
      </div>
    </template>
    <template v-if="$permissions.customWorkflowsFeatureEnabled">
      <div class="step">
        <h4>
          1. Create all possible statuses for {{ TextAssistant.itemPluralName(entityType).toLowerCase() }}
          <template v-if="!makeEntityAgnostic">in this {{ TextAssistant.entitySingularName(entityType).toLowerCase() }}</template>
        </h4>
        <div class="create-workflow-drawer__statuses-container">
          <workflow-status-item
            :entity-type="entityType"
            :status="firstStatus"
            :index="0"
            focus-on-create
          />
          <draggable
            :animation="180"
            :scroll-sensitivity="100"
            :scroll-speed="20"
            :force-fallback="true"
            :list="additionalStatuses"
            ghost-class="ghost-card"
            drag-class="drag-card"
            handle=".handle"
            class="drag-group"
            :item-key="positionKey"
          >
            <!-- Passed in index + 1 because the "first item" in statuses is firstStatus. Therefore, need to decrement by 1 when delete-clicked is emitted -->
            <template #item="{ element: status, index }">
              <workflow-status-item
                :entity-type="entityType"
                :index="index + 1"
                :status="status"
                focus-on-create
                show-drag
                show-delete
                @delete-clicked="(index: number) => removeAdditionalStatus(index - 1)"
              />
            </template>
          </draggable>
          <!-- Add status button -->
          <add-workflow-status-item
            v-if="additionalStatuses.length < maxAdditionalStatusesAllowed" 
            :entity-type="entityType"
            @clicked="addAdditionalStatus"
          />
          <workflow-status-item
            :entity-type="entityType"
            :status="lastStatus"
            :index="consolidatedStatuses.length - 1"
          />
        </div>
      </div>
      <div class="step">
        <h4>2. Name this workflow for future use</h4>
        <div class="name-and-desc">
          <div>
            <h5>Name</h5>
            <el-input v-model="workflowName" :placeholder="placeholderText" maxlength="100" />
          </div>
          <div>
            <h5>Description</h5>
            <el-input v-model="workflowDesc" maxlength="100" placeholder="Make it short and sweet..." />
          </div>
        </div>
      </div>
      <div class="create-workflow-drawer__footer">
        <dd-button
          :is-disabled="disableSubmit"
          :is-loading="isCreatingWorkflow"
          :color="btnColor" 
          size="large"
          new-icon
          @click="createWorkflow"
        >
          Create Workflow
        </dd-button>
      </div>
    </template>
    <feature-gated-stamp v-else />
  </el-drawer>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'

/* Mixins */

/* Components */
import Draggable from 'vuedraggable'

/* Helpers */
import TextAssistant from '@/helpers/text-assistant'
import ColorAssistant from '@/helpers/color-assistant'

/* Data types */
import {
  IWorkflowsInputCreate,
  IWorkflowsInputCreateStatus,
} from '@/api/types/request.types'
import { EEntities } from '@/types'

/* API */
import WorkflowsAPI from '@/api/modules/workflows.api'

export default defineComponent({
  name: 'CreateWorkflowDrawer',

  components: {
    Draggable,
  },

  props: {
    entityType: {
      type: String as () => EEntities,
      required: true,
    },
    showDrawer: {
      type: Boolean,
      default: false,
    },
    makeEntityAgnostic: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['update:showDrawer', 'workflow-created'],

  data() {
    return {
      TextAssistant,
      firstStatus: {} as IWorkflowsInputCreateStatus,
      additionalStatuses: [] as IWorkflowsInputCreateStatus[],
      lastStatus: {} as IWorkflowsInputCreateStatus,
      workflowName: '',
      workflowDesc: '',
      isCreatingWorkflow: false,
      maxAdditionalStatusesAllowed: 38, // Doesn't include first and last (so 40 total)
    }
  },

  computed: {
    visible: {
      get(): boolean {
        return this.showDrawer
      },
      set(newValue: boolean) {
        if (!newValue) {
          this.setInitialState()
        }
        this.$emit('update:showDrawer', newValue)
      },
    },
    isMailbox(): boolean {
      return this.entityType === EEntities.MAILBOXES
    },
    drawerWidthPct(): string {
      if (this.$screen.isLargeDesktop(this.$mq)) {
        return '800px'
      } else if (this.$screen.isTabletOrDesktop(this.$mq)) {
        return '75%'
      }
      return '100%'
    },
    disableSubmit(): boolean {
      // Must have at least 2 named statuses and a workflow name to submit...
      return (
        this.consolidatedStatuses.filter(s => s.name.trim() !== '').length <
          2 || this.workflowName.trim() === ''
      )
    },
    placeholderText(): string {
      return this.isMailbox
        ? 'e.g. Support workflow'
        : 'e.g. Bug tracking workflow'
    },
    btnColor(): string {
      return ColorAssistant.getEntityColor(this.entityType)
    },
    consolidatedStatuses(): IWorkflowsInputCreateStatus[] {
      return [this.firstStatus, ...this.additionalStatuses, this.lastStatus]
    },
  },

  created() {
    this.setInitialState()
  },

  methods: {
    /* New statuses have no ID, so the draggable list keys them by position */
    positionKey(status: IWorkflowsInputCreateStatus): number {
      return this.additionalStatuses.indexOf(status)
    },

    setInitialState() {
      this.firstStatus = {
        name: 'Open',
        color: 'dodger-blue',
        isInactive: false,
      } as IWorkflowsInputCreateStatus
      this.additionalStatuses = [] as IWorkflowsInputCreateStatus[]
      this.lastStatus = {
        name: 'Closed',
        color: 'bluish-grey',
        isInactive: true,
      } as IWorkflowsInputCreateStatus
      this.workflowName = ''
      this.workflowDesc = ''
    },
    addAdditionalStatus() {
      this.additionalStatuses.push({
        name: '',
        color: ColorAssistant.DefaultStatusColorOptions,
        isInactive: false,
      })
    },
    removeAdditionalStatus(index: number) {
      this.additionalStatuses.splice(index, 1)
    },

    async createWorkflow() {
      try {
        this.isCreatingWorkflow = true
        const data = {
          name: this.workflowName,
          description: this.workflowDesc,
          projectTypeID: this.entityType === EEntities.PROJECTS ? 1 : 2,
          statuses: this.consolidatedStatuses,
        } as IWorkflowsInputCreate

        const response = await WorkflowsAPI.post_Create(data)
        this.$emit('workflow-created', response.id)
        this.visible = false
      } catch (error) {
        this.$error.emit(error)
      } finally {
        this.isCreatingWorkflow = false
      }
    },
  },
})
</script>

<style lang="scss">
.create-workflow-drawer {
  &__header {
    margin: 10px 0 0 20px;

    > h4 {
      font-size: 26px !important;
    }
  }

  .step {
    margin-bottom: 20px;

    > .name-and-desc {
      display: flex;
      padding-top: 10px;
      @include flex-row-horizontal-spacing(10px);

      > div {
        flex-grow: 1;

        &:first-child {
          width: 37%;
        }
        &:last-child {
          width: 63%;
        }
      }
    }
  }

  &__statuses-container {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding-top: 10px;

    .drag-group {
      width: 100%;
    }

    .ghost-card {
      cursor: grab !important;
      opacity: 0.5 !important;
      > * {
        visibility: hidden;
      }
    }

    .drag-card {
      cursor: grab !important;
      transform: rotate(-0.4deg);
      z-index: 2000;
    }
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    padding-top: 20px;
    width: 100%;
  }
}
</style>
